// frontend/src/components/CreaVarianteDialog.js
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography
} from '@mui/material';
import api from '../api';

const CONDIZIONI = [
    "Nuovo",
    "Usato - Ottime Condizioni",
    "Usato - Buone Condizioni",
    "Usato - Discrete Condizioni",
    "Usato - Accettabile"
];

const SUPPORTI_FILM = ["DVD", "Blu-Ray", "4K", "VHS"];
const SUPPORTI_MUSICA = ["CD", "Vinile", "Musicassetta"];


const CreaVarianteDialog = ({ open, onClose, barcode, onVarianteCreata }) => {
    const [metadata, setMetadata] = useState(null);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open && barcode) {
            const fetchMetadata = async () => {
                setLoading(true);
                setError(null);
                setMetadata(null);
                try {
                    const response = await api.get(`/utils/product-metadata/${barcode}`);
                    setMetadata(response.data);
                    // Pre-compila il form
                    setFormData({
                        ...response.data,
                        condizione: '',
                        prezzo_negozio: '',
                        quantita: 1, // Default a 1
                        ubicazione: ''
                    });
                } catch (err) {
                    setError('Impossibile caricare i metadati per questo barcode. Potrebbe essere un prodotto totalmente nuovo.');
                    setFormData({ barcode }); // Se non ci sono meta, imposta almeno il barcode
                } finally {
                    setLoading(false);
                }
            };
            fetchMetadata();
        }
    }, [open, barcode]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);

        // Validazione base
        if (!formData.condizione || !formData.prezzo_negozio) {
            setError("Condizione e Prezzo Negozio sono campi obbligatori.");
            setLoading(false);
            return;
        }

        const endpoint = metadata?.item_type === 'Film' ? '/film' : '/musica';
        
        // Se non ci sono metadati, probabilmente è un prodotto nuovo
        if (!metadata) {
            setError("Funzionalità non ancora implementata: creare un prodotto da zero.");
            setLoading(false);
            return;
        }

        try {
            const response = await api.post(endpoint, {
                ...formData,
                // Assicurati che i campi numerici siano numeri
                prezzo_negozio: parseFloat(formData.prezzo_negozio) || 0,
                quantita: parseInt(formData.quantita) || 1,
            });
            onVarianteCreata(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || err.message || "Errore durante il salvataggio.");
        } finally {
            setLoading(false);
        }
    };

    return (
                <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                        <DialogTitle>Crea Nuova Variante Prodotto</DialogTitle>
                        <DialogContent>
                                {loading && !metadata && <CircularProgress />}
                                {error && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {Array.isArray(error)
                                            ? (
                                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                    {error.map((e, i) => (
                                                        <li key={i}>{e.msg || JSON.stringify(e)}</li>
                                                    ))}
                                                </ul>
                                            )
                                            : (typeof error === 'object' && error !== null)
                                                ? JSON.stringify(error)
                                                : error
                                        }
                                    </Alert>
                                )}
                
                                {metadata && (
                    <Box>
                        <Typography variant="h6">{metadata.titolo || metadata.artista}</Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Barcode: {barcode}
                        </Typography>

                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12}>
                                <FormControl fullWidth required>
                                    <InputLabel>Condizione</InputLabel>
                                    <Select
                                        name="condizione"
                                        value={formData.condizione || ''}
                                        onChange={handleChange}
                                        label="Condizione"
                                    >
                                        {CONDIZIONI.map(c => (
                                            <MenuItem key={c} value={c}>{c}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    name="prezzo_negozio"
                                    label="Prezzo Negozio"
                                    type="number"
                                    value={formData.prezzo_negozio || ''}
                                    onChange={handleChange}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    name="quantita"
                                    label="Quantità"
                                    type="number"
                                    value={formData.quantita || ''}
                                    onChange={handleChange}
                                    fullWidth
                                    defaultValue={1}
                                />
                            </Grid>
                             <Grid item xs={12}>
                                <TextField
                                    name="ubicazione"
                                    label="Ubicazione (es. DVD USATI LIB)"
                                    value={formData.ubicazione || ''}
                                    onChange={handleChange}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Annulla</Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    disabled={loading || !metadata}
                    startIcon={loading ? <CircularProgress size={18} /> : null}
                >
                    {loading ? 'Salvataggio...' : 'Salva Variante'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreaVarianteDialog;
